import { useEffect, useState } from 'react';
import { Check, Lock } from 'lucide-react';
import Modal from './Modal';
import { startCaseCheckout, type StartCasePlan } from '../lib/freeAccount';
import { phFailure, phInitiateCheckout, phPaywallDismissed, phPaywallViewed } from '../lib/posthog';
import '@styles/components/start-case-paywall.css';

/**
 * The pay popup for free accounts. Shown right after a free upload, and
 * whenever a free account opens something that needs a plan. Picking a
 * plan goes straight to Stripe with the account email already filled in.
 */

const PLANS: Record<
  StartCasePlan,
  { name: string; price: number; per: string; points: string[] }
> = {
  one_off: {
    name: 'Pay Per Bill',
    price: 129,
    per: 'one bill',
    points: [
      'A specialist checks every line and deals with the hospital. No calls for you.',
      "Refunded in full the day your case closes if we don't save you at least $129.",
    ],
  },
  annual: {
    name: 'Annual Plan',
    price: 499,
    per: 'up to 6 bills a year',
    points: [
      'Every bill for your household this year, each checked line by line.',
      "Refunded in full if your bills aren't lowered by at least $499 combined.",
    ],
  },
};

type Props = {
  open: boolean;
  onClose: () => void;
  email: string | null | undefined;
  /** True when this account already has a bill uploaded and waiting. */
  hasDraft: boolean;
  /** Name of the uploaded file. Not shown any more, kept so callers don't change. */
  fileName?: string | null;
  /** YYYY-MM-DD from the upload form, if they gave one. */
  dueDate?: string | null;
  /** What opened the popup, for analytics ("after_upload", "waiting_card", "plan_page"...). */
  trigger: string;
  /** A line pointing at the Annual plan when this person looks like they'll
   *  have more than one bill, e.g. several pages or family added. */
  annualHint?: string | null;
  /** Used to open chat from here. The popup has no chat link now, kept so callers don't change. */
  onAsk?: () => void;
};

/** "Oct 19 · 9 days" for the slip, or null when no date was given. */
function dueLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const due = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const days = Math.ceil((due.getTime() - new Date().getTime()) / (24 * 3600 * 1000));
  const date = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days < 0) return `${date} · past due`;
  if (days === 0) return `${date} · today`;
  return `${date} · ${days} day${days === 1 ? '' : 's'}`;
}

export default function StartCasePaywall({ open, onClose, email, hasDraft, dueDate, trigger, annualHint }: Props) {
  const [plan, setPlan] = useState<StartCasePlan>(() => {
    // The plan they clicked on the marketing site before signing up.
    try {
      return localStorage.getItem('wanted_plan') === 'annual' ? 'annual' : 'one_off';
    } catch {
      return 'one_off';
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) phPaywallViewed({ trigger, hasDraft, plan, price: PLANS[plan].price });
    // Only when it opens. Switching plans inside the popup isn't a new view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trigger, hasDraft]);

  const selected = PLANS[plan];

  const close = () => {
    if (loading) return;
    phPaywallDismissed({ trigger });
    onClose();
  };

  const pay = async () => {
    setError(null);
    setLoading(true);
    phInitiateCheckout('start_case', { flow: `free_to_${plan}`, value: selected.price, plan });
    try {
      await startCaseCheckout(plan, email);
      // The browser is on its way to Stripe, keep the button busy.
    } catch (err) {
      phFailure({
        step: 'start_case_checkout',
        message: err instanceof Error ? err.message : 'unknown',
        context: { plan },
      });
      const msg = err instanceof Error ? err.message : '';
      setError(
        msg === 'network'
          ? "We couldn't reach our server. Check your connection and try again."
          : 'We could not open the payment page. Try again in a moment.',
      );
      setLoading(false);
    }
  };

  const due = dueLabel(dueDate);

  /* Both plans side by side as two small tiles, so each price shows up once
     and there's nothing else pitching Annual. Then two lines, one button and
     the Stripe line (2026-10-27, the old version said $129 three times). */
  return (
    <Modal open={open} onClose={close} className="scp" showClose closeLabel="Not now">
      <h3 className="scp-title">Start your case</h3>
      {hasDraft && due && <p className="scp-file">Bill due {due}</p>}

      <div className="scp-options" role="radiogroup" aria-label="Plan">
        {(['one_off', 'annual'] as StartCasePlan[]).map((key) => {
          const p = PLANS[key];
          const on = plan === key;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={on}
              className={`scp-option${on ? ' is-on' : ''}`}
              onClick={() => setPlan(key)}
              disabled={loading}
              title={key === 'annual' && annualHint ? annualHint : undefined}
            >
              {key === 'annual' && annualHint && <span className="scp-option-tag">Fits your family</span>}
              <span className="scp-option-name">{p.name}</span>
              <span className="scp-option-price">${p.price}</span>
              <span className="scp-option-per">{p.per}</span>
            </button>
          );
        })}
      </div>

      <ul className="scp-points">
        {selected.points.map((point) => (
          <li key={point}>
            <Check size={16} strokeWidth={2.5} aria-hidden="true" />
            {point}
          </li>
        ))}
      </ul>

      {error && <p className="scp-error">{error}</p>}

      <button type="button" className="scp-pay" onClick={pay} disabled={loading}>
        {loading ? 'Opening secure payment…' : 'Continue to payment'}
      </button>

      <p className="scp-foot">
        <Lock size={12} aria-hidden="true" /> Secure payment by Stripe
      </p>
    </Modal>
  );
}
